import { ASSETS_VIEW_SEARCH_PARAM } from "./components/AssetsViewToggle/AssetsViewToggle.constants";

export const ROW_SEARCH_PARAM = "row" as const;

export const PATH_FILTER_KEY = "path" as const; // because `getItemId` in `NodeTree` uses `path`

/** Chart display toggle in Metrics tab (?chart-type=) */
export const CHART_TYPE_SEARCH_PARAM = "chart-type" as const;

/** Metrics-table group filter (?metricGroupType=). Default / absent = all. */
export const METRIC_GROUP_TYPE_SEARCH_PARAM = "metricGroupType" as const;

/** Debug mode: collapsed assets panel (boolean; absent = false) */
export const COLLAPSE_TREE_SEARCH_PARAM = "collapse-tree" as const;

/**
 * Edit-test drawer (`useEditTestDrawer` in atoms.ts).
 *
 * Open ⇔ value is `true` (read via `useBooleanSearchParam`). The drawer's target row
 * is always `?row=` (encoded `uniqueKey`); this param is a presence flag only — no payload.
 * `openFor` writes `?row=` and `?edit-test=true` in one `setSearchParams` updater.
 */
export const EDIT_TEST_SEARCH_PARAM = "edit-test" as const;

/**
 * Add/Edit-metric drawer view (`useMetricDrawer` in atoms.ts).
 *
 * Open ⇔ value present. Values: `addMetric` | `editMetric`.
 */
export const METRIC_DRAWER_SEARCH_PARAM = "metric-drawer" as const;

/**
 * Back-button behaviour for the editMetric inner view: `back` returns to the
 * addMetric list inside the drawer; `root` closes the drawer entirely.
 * Only meaningful when `metric-drawer === editMetric`.
 */
export const METRIC_DRAWER_BACK_SEARCH_PARAM = "metric-drawer-back" as const;

/**
 * Target metric for the editMetric inner view, JSON-encoded `AssetMetricType`
 * (URL-encoded). Only meaningful when `metric-drawer === editMetric`.
 *
 * JSON-in-URL beats three sibling params here because the three fields are one
 * logical fact and must move together; one value = no inter-key desync window.
 */
export const METRIC_DRAWER_TARGET_SEARCH_PARAM = "metric-drawer-target" as const;

/**
 * Metrics-tab query keys stripped on pathname change (see useClearSearchParamsOnPathnameChange)
 * and when leaving Metrics tab (PipelinePageTabs cleanupSearchParamsByTab).
 *
 * When adding new `location.search` state for Metrics, extend this tuple.
 */
export const METRICS_TAB_QUERY_PARAMS = [
  PATH_FILTER_KEY, // because `getItemId` in `NodeTree` uses `path`
  ROW_SEARCH_PARAM,
  COLLAPSE_TREE_SEARCH_PARAM,
  ASSETS_VIEW_SEARCH_PARAM,
  CHART_TYPE_SEARCH_PARAM,
  EDIT_TEST_SEARCH_PARAM,
  METRIC_DRAWER_SEARCH_PARAM,
  METRIC_DRAWER_BACK_SEARCH_PARAM,
  METRIC_DRAWER_TARGET_SEARCH_PARAM,
  METRIC_GROUP_TYPE_SEARCH_PARAM,
] as const satisfies readonly string[];

/**
 * Prefix applied to compound (group) row uniqueKeys. Differentiates compound ids from
 * leaf-metric ids (which look like `${metric_id}` or `${metric_id},${asset_value}`) and
 * keeps `?row=` short and URL-safe. The id body is the smallest metric_id of the group —
 * stable across reloads and globally unique.
 */
export const COMPOUND_ROW_ID_PREFIX = "g" as const;

/**
 * Canonical separator for asset-hierarchy paths everywhere in the codebase
 * (`data|nba_output|blowouts|blowouts`). Used by tree builders, the
 * `startsWith` filter operator, NodeTree paths, etc. Do NOT change without a
 * codebase-wide migration.
 */
export const ASSET_PATH_DELIMITER = "|" as const;

/**
 * URL-only separator for `?path=`, swapped in/out at the search-param boundary
 * (see `useAssetFilters` in `filterModelAtom.ts`). The internal path format
 * stays `|`-delimited; this affects the URL representation only.
 *
 * Why `~`:
 * - `URLSearchParams.toString()` percent-encodes everything outside `* - . _`
 *   plus alphanumerics, so `|` becomes `%7C` (ugly: `?path=data%7Cnba…`).
 * - `~` does percent-encode to `%7E` in the stored URL, but every major
 *   browser renders `%7E` back as `~` in the address bar (RFC 3986 marks `~`
 *   as unreserved → safe to display-decode), so the URL the user sees is
 *   `?path=data~nba_output~blowouts`.
 * - Asset names commonly use `_` (snake), `-` (kebab) or `.` (dotted
 *   schemas) — those would collide with the path components themselves.
 *   `~` essentially never appears in real asset names.
 *
 * Read direction is lenient: we only rewrite `~` → `|`, so an old bookmark
 * carrying a literal `|` (e.g. `?path=data|x`, persisted as `%7C`) still
 * round-trips to the same internal path.
 */
export const ASSET_PATH_URL_DELIMITER = "~" as const;

/**
 * URL-only separator for the `,` inside a row uniqueKey (e.g.
 * `<metric_id>,<asset_value>` → `<metric_id>~<asset_value>` in the URL).
 * Swapped in/out at the search-param boundary in `MetricsTabView.utils.tsx`; the internal
 * uniqueKey format stays comma-joined (see `getMetricUniqueKey`).
 *
 * Same `~` rationale as `ASSET_PATH_URL_DELIMITER`: every major browser
 * renders `%7E` back as `~` in the address bar, and `~` essentially never
 * appears in real `metric_id`s / `asset_value`s. Without this swap the URL
 * shows the comma percent-encoded as `%2C` (`?row=656158%2Clost`).
 *
 * Read direction is lenient: only `~` → `,` is applied, so old bookmarks
 * carrying a literal `,` (persisted as `%2C`) still round-trip to the same
 * internal uniqueKey.
 */
export const ROW_KEY_URL_DELIMITER = "~" as const;
